import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { NotificationContext } from '../../../../../../../NotificationContext';
import { expressMutation, useExpressQuery, getQueryKey } from '../../../../../../../repository/react-query/useExpress';

const baseQueryKey = 'hauler-yards';
const baseEndpoint = '/hauler-yards';
const GEOFENCE_QUERY_KEY = 'geofences';

const useFetchHaulerYards = ({ enabled = false, hauler_id, route = '' } = {}) => {
    return useQuery({
        queryKey: [baseQueryKey, getQueryKey({ hauler_id })],
        queryFn: useExpressQuery({ endpoint: `${baseEndpoint}${route}`, params: { where: { hauler_id } } }),
        enabled,
    });
};

export const useCreateHaulerYard = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: (model) =>
            expressMutation({
                method: 'post',
                endpoint: baseEndpoint,
                body: model,
            }),
        onError: () => {
            showSnackbar({ severity: 'error', message: 'Failed to create yard' });
        },
        onSuccess: () => {
            showSnackbar({ severity: 'success', message: 'Yard created successfully' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
            queryClient.invalidateQueries({ queryKey: [GEOFENCE_QUERY_KEY] });
        },
    });
};

export const useMutateHaulerYard = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: ({ _id, ...model }) =>
            expressMutation({
                method: 'patch',
                endpoint: `${baseEndpoint}/${_id}`,
                body: model,
            }),
        onMutate: async (newItem) => {
            await queryClient.cancelQueries({ queryKey: [baseQueryKey] });
            const queries = queryClient.getQueryCache().findAll({ queryKey: [baseQueryKey] });
            queries.forEach((query) => {
                const previousData = query.state.data;
                if (Array.isArray(previousData)) {
                    queryClient.setQueryData(
                        query.queryKey,
                        previousData.map((item) => (item._id === newItem._id ? { ...item, ...newItem } : item))
                    );
                }
            });
            return { queries };
        },
        onError: (err, _, ctx) => {
            if (ctx?.queries) {
                ctx.queries.forEach((query) => {
                    queryClient.setQueryData(query.queryKey, query.state.data);
                });
            }
            showSnackbar({ severity: 'error', message: 'Failed to update yard. Please try again.' });
        },
        onSuccess: () => {
            showSnackbar({ severity: 'success', message: 'Yard updated successfully' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
            queryClient.invalidateQueries({ queryKey: [GEOFENCE_QUERY_KEY] });
        },
    });
};

export const useDeleteHaulerYard = () => {
    const queryClient = useQueryClient();
    const { showSnackbar } = useContext(NotificationContext);
    return useMutation({
        mutationFn: ({ _id }) =>
            expressMutation({
                method: 'delete',
                endpoint: `${baseEndpoint}/${_id}`,
            }),
        onError: (err) => {
            const res = err?.response?.data;
            showSnackbar({ severity: 'error', message: res?.message ?? 'Failed to delete yard' });
        },
        onSuccess: () => {
            showSnackbar({ severity: 'success', message: 'Yard deleted successfully' });
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [baseQueryKey], type: 'all' });
            queryClient.invalidateQueries({ queryKey: [GEOFENCE_QUERY_KEY] });
        },
    });
};

export default useFetchHaulerYards;
